import { QueryClient } from '@tanstack/react-query';
import { Promotion, Sales, Stock, PlugPag, Store } from '@inhouse-market/sdk';
import { ParamsStoreManagement } from './Store/types';
import { RequestParamsGetProducts } from './Product/types';
import { FiltersChurn } from './Churn/types';
import { ClientsListParams } from './Clients/types';
import { CouponFilters } from './Coupon/types';
import { BillingRulesParams } from './Billing/types';
import { GetPurchaseParams } from './Purchases/type';
import {
    InventoryListParams,
    InventoryItemsParams,
    InventorySummaryFilters,
} from './Inventory/types';

const queryClient = new QueryClient();

export const queryKeys = {
    GET_SETTINGS: ['GET_SETTINGS'],
    GET_LOGS_RELOAD_SETTINGS: ['GET_LOGS_RELOAD_SETTINGS'],
    GET_CLIENTS_PAGINATED: (email: string | undefined) => [
        'GET_CLIENTS_PAGINATED',
        email,
    ],
    GET_CLIENT_BLOCK_HISTORY: (userId?: number) => [
        'GET_CLIENT_BLOCK_HISTORY',
        userId,
    ],
    GET_SHOPMANAGER_TOTEMS: ['GET_SHOPMANAGER_TOTEMS'],
    GET_TOTEM_REPORT: (params: PlugPag.ReportRequest) => [
        'GET_TOTEM_REPORT',
        params,
    ],
    GET_STORES_V2: (params: Store.SearchRequest) => ['GET_STORES_V2', params],
    GET_STORE_LOCKS: (params: Record<string, any>) => [
        'GET_STORE_LOCKS',
        params,
    ],
    GET_ME: ['GET_ME'],
    GET_PRODUCTS_PAGINATED: (params?: RequestParamsGetProducts) => [
        'GET_PRODUCTS_PAGINATED',
        params,
    ],
    SEARCH_STORE_PLANOGRAM: (
        storeId: string | number | undefined,
        productName: string,
    ) => ['SEARCH_STORE_PLANOGRAM', storeId, productName],
    GET_AUTH_STORE: () => ['GET_AUTH_STORE'],
    SALES: (params: Sales.SalesListByTransactionRequest) =>
        ['sales', params] as const,
    SALES_BY_TRANSACTION: (params: Sales.SalesListByTransactionRequest) =>
        ['sales-by-transaction', params] as const,
    EXTRA_INFO: (params: Sales.SalesGetExtraInfoRequest) =>
        ['extra-info', params] as const,
    SUMMARY: (params: Sales.SalesGetExtraInfoSummaryRequest) =>
        ['summary', params] as const,
    SALE: (id: number) => ['sale', id] as const,
    STORE_GROUPS: ['STORE_GROUPS'],
    STORES_BY_GROUP_ROOT: ['STORES_BY_GROUP'],
    STORES_BY_GROUP: (name: string) => ['STORES_BY_GROUP', name],
    SUMMARY_CHURN: ['SUMMARY_CHURN'],
    CHURNS: (params?: FiltersChurn) => ['CHURNS', params],
    CLIENTS: (params?: ClientsListParams) => ['CLIENTS', params],
    CHURN_PARAMS: ['CHURN_PARAMS'],
    BILLING_RULES: (params: BillingRulesParams) => ['BILLING_RULES', params],
    GLOBAL_BILLING_RULES: (shopManagerId: number) => [
        'GLOBAL_BILLING_RULES',
        shopManagerId,
    ],
    STORE_MANAGEMENT_SUMMARY: (params?: ParamsStoreManagement) => [
        'STORE_MANAGEMENT_SUMMARY',
        params,
    ],
    INTERNAL_NOTIFICATION_SYSTEM_SETTINGS: [
        'INTERNAL_NOTIFICATION_SYSTEM_SETTINGS',
    ],
    PROMOTIONS_ROOT: ['PROMOTIONS'],
    PROMOTIONS: (filters?: Promotion.GetAllRequest) => ['PROMOTIONS', filters],
    PROMOTION: (id?: number) => ['PROMOTION', id],
    COUPONS_ROOT: ['COUPONS'],
    CLIENTS_ROOT: ['CLIENTS'],
    INVOICES_V2_ROOT: ['INVOICES_V2'],
    COUPONS: (filters?: CouponFilters) => ['COUPONS', filters],
    PURCHASES: (params?: GetPurchaseParams) => ['PURCHASES', params],
    PURCHASE: (id?: number) => ['PURCHASE', id],
    STOCK_TURNOVER: (params: Stock.StockTurnoverRequest) => [
        'STOCK_TURNOVER',
        params,
    ],
    STOCK_OUT: (params: Stock.StockOutRequest) => ['STOCK_OUT', params],
    STOCK_OUT_SUMMARY: (params: Stock.StockOutSummaryRequest) => [
        'STOCK_OUT_SUMMARY',
        params,
    ],
    LICENSE_TYPES: ['LICENSE_TYPES'],
    SHOPMANAGER_ALL: (shopManagerId: number) => [
        'SHOPMANAGER_ALL',
        shopManagerId,
    ],
    INVENTORIES: (params: InventoryListParams) => ['INVENTORIES', params],
    INVENTORY_SUMMARY: (filters: InventorySummaryFilters) => [
        'INVENTORY_SUMMARY',
        filters,
    ],
    INVENTORY: (inventoryId: number) => ['INVENTORY', inventoryId],
    INVENTORY_PENDING_CATEGORIES: (storeId: number, categoryIds: number[]) => [
        'INVENTORY_PENDING_CATEGORIES',
        storeId,
        categoryIds,
    ],
    INVENTORY_ITEMS: (inventoryId: number, params: InventoryItemsParams) => [
        'INVENTORY_ITEMS',
        inventoryId,
        params,
    ],
};

export default queryClient;
